import type { components } from "@majitap/sdk";
import {
  ActivityTimeline,
  Button,
  formatDateTime,
  formatMoney,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { XCircle } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { useActivity } from "../../activity";
import { api, errorOf } from "../../api";
import { useProductItems } from "../../catalog";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { messageFor } from "../../error-copy";
import { invoiceActions } from "../../invoices";
import { isCreditTerm } from "../../labels";
import { meQuery } from "../../session";
import { PaymentsPanel } from "../shared/finance";
import { ReasonFields, useReasonPicker } from "../shared/reason-fields";
import { MarkUnpaidDialog, PostpaidDialog, RecordPaymentDialog, SendStkDialog } from "./collect";
import { Figures, liveDeliveries } from "./figures";
import { DeliveriesSection } from "./order-deliveries";
import { CustomerCard, DetailsCard, SideCard } from "./order-side";
import styles from "./orders.module.css";
import { type CollectKind, PaymentCard } from "./pay-card";
import page from "./record.module.css";

/** Finished orders have nothing left to cancel. For the rest the database decides, and its refusal is shown. */
const FINISHED = new Set(["delivered", "cancelled", "failed"]);

const orderQuery = (id: string) => ({
  queryKey: ["order", id],
  queryFn: async () => {
    const { data, error } = await api.GET("/v1/admin/orders/{id}", { params: { path: { id } } });
    if (error || !data) throw errorOf(error);
    return data;
  },
});

const termsQuery = {
  queryKey: ["payment-terms"],
  staleTime: 5 * 60_000,
  queryFn: async () => {
    const { data, error } = await api.GET("/v1/admin/billing/payment-terms");
    if (error || !data) throw errorOf(error);
    return data.data;
  },
};

/**
 * One order (P8-07; P8-48 `Console / Order / Redesign`): the figures, its deliveries, what it
 * costs and earns, payments, and on the side who it's for, the payment, details and history.
 */
export function OrderPage(): ReactNode {
  const { orderId } = useParams({ from: "/console/orders/$orderId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const format = useCompanyFormat();
  const order = useQuery(orderQuery(orderId));
  const terms = useQuery(termsQuery);
  const items = useProductItems();
  const permissions = me?.permissions ?? [];
  const activity = useActivity("order", orderId, permissions.includes("staff.manage"));
  const [cancelling, setCancelling] = useState(false);
  const [collecting, setCollecting] = useState<CollectKind | null>(null);
  useRecordCrumb(order.data?.order_number);

  if (order.status === "pending") return <PageLoading label="Loading the order" />;
  if (order.status === "error") {
    const error = errorOf(order.error);
    if (error.code === "ORDER_NOT_FOUND") {
      return <PageEmpty title="No such order" description="It may be in another company, or the link is wrong." />;
    }
    return <PageError message={messageFor(error)} onRetry={() => order.refetch()} />;
  }

  const o = order.data;
  const term = terms.data?.find((t) => t.id === o.payment_term_id);
  const canCancel = permissions.includes("orders.write") && !FINISHED.has(o.status);
  // any order can have its own invoice, delivered or not, paid or not, except while a payment is
  // under way (2026-11-03); with nothing left to bill, the API says so (NOTHING_TO_INVOICE)
  const invoiceable = !["pending", "disputed"].includes(o.payment_status);
  const canInvoice =
    permissions.includes("invoices.issue") &&
    invoiceable &&
    o.deliveries.some((d) => !["cancelled", "failed"].includes(d.status));
  // collecting the payment (P8-39): money is still owed on an order that isn't cancelled
  const owed = o.status !== "cancelled" && !["paid", "refunded"].includes(o.payment_status);
  // a refunded order can take a new payment, or be marked unpaid to collect again
  const refunded = o.status !== "cancelled" && o.payment_status === "refunded";
  // once on an invoice, it is collected on the invoice (ORDER_INVOICED, P8-47)
  const canCollect = permissions.includes("payments.record") && (owed || refunded) && o.invoices.length === 0;

  return (
    <>
      <PageHeader
        title={o.order_number}
        description={`${o.business?.name ?? o.customer.name} · placed ${formatDateTime(o.created_at, format)}`}
        actions={canInvoice ? <InvoiceOrder orderId={o.id} /> : undefined}
      />
      {o.status === "cancelled" ? (
        <Alert tone="info" title="This order was cancelled">
          {[
            o.cancelled_at ? formatDateTime(o.cancelled_at, format) : null,
            o.cancellation?.by ? `by ${o.cancellation.by.name}` : null,
            o.cancellation?.reason?.name,
            // the note, when it says more than the reason picked
            o.cancel_reason !== o.cancellation?.reason?.name ? o.cancel_reason : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Alert>
      ) : null}
      {o.postpaid && owed ? (
        <Alert tone="warning" title="Postpaid: collect the payment">
          {[formatDateTime(o.postpaid.at, format), o.postpaid.reason].join(" · ")}
        </Alert>
      ) : null}
      {o.marked_unpaid && owed ? (
        <Alert tone="warning" title="Marked unpaid after a refund: collect the payment">
          {[formatDateTime(o.marked_unpaid.at, format), o.marked_unpaid.reason].join(" · ")}
        </Alert>
      ) : null}
      <Figures order={o} termName={term?.name ?? null} />
      <div className={page.columns}>
        <div className={page.main}>
          <DeliveriesSection order={o} />
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Items and charges</h2>
            <SortableDataTable
              caption="Items and charges"
              rowKey="key"
              rows={chargeRows(o, items.labelOf)}
              itemName={{ one: "line", other: "lines" }}
              columns={[
                { key: "label", label: "Item", sortable: false },
                { key: "quantity", label: "Quantity", sortable: false },
                { key: "unit", label: "Unit price", sortable: false, render: (_v, r) => formatMoney(r.unit, format) },
                { key: "amount", label: "Amount", sortable: false, render: (_v, r) => formatMoney(r.amount, format) },
              ]}
            />
            <div className={styles.totals}>
              <span className={styles.totalRow}>
                Subtotal <span className={styles.amount}>{formatMoney(o.subtotal_amount, format)}</span>
              </span>
              {o.discount_amount !== "0.00" ? (
                <span className={styles.totalRow}>
                  Discount <span className={styles.amount}>{formatMoney(o.discount_amount, format)}</span>
                </span>
              ) : null}
              <span className={styles.totalRow}>
                <strong>Total</strong> <span className={styles.amount}>{formatMoney(o.total_amount, format)}</span>
              </span>
            </div>
          </section>

          <CostsSection order={o} />

          {permissions.includes("payments.record") ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Payments</h2>
              <PaymentsPanel
                filter={{ order_id: o.id }}
                empty="Payments for this order, and refunds of them, show here. Open one to refund it."
              />
            </section>
          ) : null}
        </div>

        <aside className={page.side}>
          <CustomerCard order={o} />
          <PaymentCard
            order={o}
            termName={term?.name ?? null}
            onCredit={isCreditTerm(term?.kind)}
            owed={owed}
            canCollect={canCollect}
            onCollect={setCollecting}
          />
          <DetailsCard order={o} />
          {activity.data ? (
            <SideCard title="Activity">
              <ActivityTimeline entries={activity.data} timeZone={format.timezone} />
            </SideCard>
          ) : null}
          {canCancel ? (
            <button type="button" className={page.dangerLink} onClick={() => setCancelling(true)}>
              <XCircle size={16} aria-hidden /> Cancel this order
            </button>
          ) : null}
        </aside>
      </div>
      <CancelOrder orderId={o.id} orderNumber={o.order_number} open={cancelling} onOpenChange={setCancelling} />
      {collecting === "record" ? (
        <RecordPaymentDialog target={{ order_id: o.id }} number={o.order_number} onClose={() => setCollecting(null)} />
      ) : null}
      {collecting === "stk" ? (
        <SendStkDialog
          orderId={o.id}
          orderNumber={o.order_number}
          phone={o.customer.phone_number}
          onClose={() => setCollecting(null)}
        />
      ) : null}
      {collecting === "postpaid" ? (
        <PostpaidDialog orderId={o.id} orderNumber={o.order_number} onClose={() => setCollecting(null)} />
      ) : null}
      {collecting === "unpaid" ? (
        <MarkUnpaidDialog orderId={o.id} orderNumber={o.order_number} onClose={() => setCollecting(null)} />
      ) : null}
    </>
  );
}

type AdminOrder = components["schemas"]["AdminOrder"];

/**
 * The lines that make up the subtotal: the products ordered, then each delivery's other charges
 * (delivery fee, surcharges). Product prices come from the items; discounts show in the totals.
 */
function chargeRows(o: AdminOrder, labelOf: (id: string) => string) {
  const products = o.items.map((i) => ({
    key: i.id,
    label: labelOf(i.product_item_id),
    quantity: i.quantity,
    unit: i.unit_price,
    amount: i.amount,
  }));
  const charges = o.deliveries.flatMap((d) =>
    d.lines
      .filter((l) => l.direction === "receivable" && !["product_price", "discount"].includes(l.charge_type.code))
      .map((l, n) => ({
        key: `${d.id}-${n}`,
        label: `${l.description ?? l.charge_type.name}${o.deliveries.length > 1 ? ` · ${d.delivery_number}` : ""}`,
        quantity: l.quantity,
        unit: l.unit_amount,
        amount: l.amount,
      })),
  );
  return [...products, ...charges];
}

/**
 * A draft invoice for the order's deliveries not yet on one, delivered or not (P8-14). A paid
 * order's comes to 0. It opens for review; nothing is owed until it's issued.
 */
function InvoiceOrder({ orderId }: { orderId: string }) {
  const navigate = useNavigate();
  const [problem, setProblem] = useState<string>();
  const make = useMutation({
    mutationFn: () => invoiceActions.fromOrder(orderId),
    onSuccess: (draft) => navigate({ to: "/invoices/$invoiceId", params: { invoiceId: draft.id } }),
    onError: (e) => setProblem(messageFor(errorOf(e))),
  });
  return (
    <>
      <Button variant="secondary" loading={make.isPending} onClick={() => make.mutate()}>
        Invoice the order
      </Button>
      {problem ? (
        <Dialog open onOpenChange={(open) => !open && setProblem(undefined)}>
          <DialogContent title="No invoice made" description={problem}>
            <div className={styles.toolbarEnd}>
              <Button variant="secondary" onClick={() => setProblem(undefined)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

/** Cancel with a reason. Refusals (a delivery already on its way) come from the database. */
function CancelOrder({
  orderId,
  orderNumber,
  open,
  onOpenChange,
}: {
  orderId: string;
  orderNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const reason = useReasonPicker("order_cancelled");
  const [tried, setTried] = useState(false);
  const cancel = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST("/v1/admin/orders/{id}/cancel", {
        params: { path: { id: orderId } },
        body: { reason: reason.text, reason_id: reason.reasonId },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      onOpenChange(false);
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (!reason.missing) cancel.mutate();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={`Cancel ${orderNumber}?`} description="Every delivery not yet under way is cancelled too.">
        <form onSubmit={submit} className={styles.form}>
          {cancel.error ? (
            <Alert tone="danger" title="The order wasn't cancelled">
              {messageFor(errorOf(cancel.error))}
            </Alert>
          ) : null}
          <ReasonFields picker={reason} showMissing={tried} />
          <div className={styles.toolbarEnd}>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Keep the order
            </Button>
            <Button type="submit" variant="danger" loading={cancel.isPending}>
              Cancel order
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** What MajiTap pays for the order: each delivery's cost lines (haulage, water), staff only. */
function CostsSection({ order: o }: { order: AdminOrder }) {
  const format = useCompanyFormat();
  // the same deliveries the API counts in costs_total: a rerouted or cancelled one costs nothing
  const rows = liveDeliveries(o).flatMap((d) =>
    d.lines
      .filter((l) => l.direction === "payable")
      .map((l, n) => ({
        key: `${d.id}-${n}`,
        label: `${l.description ?? l.charge_type.name}${o.deliveries.length > 1 ? ` · ${d.delivery_number}` : ""}`,
        amount: l.amount,
      })),
  );
  if (rows.length === 0) return null;
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Costs</h2>
      <p className={styles.muted}>What MajiTap pays for these deliveries: the truck owner and the fill site.</p>
      <SortableDataTable
        caption="Costs"
        rowKey="key"
        rows={rows}
        itemName={{ one: "cost", other: "costs" }}
        columns={[
          { key: "label", label: "Cost", sortable: false },
          { key: "amount", label: "Amount", sortable: false, render: (_v, r) => formatMoney(r.amount, format) },
        ]}
      />
      <div className={styles.totals}>
        <span className={styles.totalRow}>
          <strong>Costs</strong> <span className={styles.amount}>{formatMoney(o.costs_total, format)}</span>
        </span>
        <span className={styles.totalRow}>
          <strong>Margin before tax</strong>{" "}
          <span className={`${styles.amount} ${page.marginValue}`}>{formatMoney(o.margin, format)}</span>
        </span>
      </div>
    </section>
  );
}
